import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import logger from '../utils/logger';

const DocumentPreview = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        
        // Fetch document info with share token
        const response = await axios.get(
          `${baseURL}/documents/${id}/preview?token=${token}`,
          {
            responseType: 'blob',
          }
        );

        // Create object URL for the blob
        const fileURL = URL.createObjectURL(response.data);
        const contentType = response.headers['content-type'];
        
        setDocument({
          url: fileURL,
          type: contentType,
        });
        setLoading(false);
      } catch (err) {
        logger.error('Error fetching document:', err);
        setError(err.response?.data?.message || 'فشل في تحميل المستند');
        setLoading(false);
      }
    };

    if (id && token) {
      fetchDocument();
    } else {
      setError('رابط غير صالح');
      setLoading(false);
    }

    // Cleanup blob URL on unmount
    return () => {
      if (document?.url) {
        URL.revokeObjectURL(document.url);
      }
    };
  }, [id, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل المستند...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">خطأ</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            قد يكون الرابط منتهي الصلاحية أو غير صحيح
          </p>
        </div>
      </div>
    );
  }

  const isPDF = document?.type === 'application/pdf';
  const isImage = document?.type?.startsWith('image/');

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h1 className="text-xl font-semibold text-gray-800">معاينة المستند المشترك</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {isPDF && (
            <iframe
              src={document.url}
              title="معاينة PDF"
              className="w-full h-screen"
              style={{ minHeight: '80vh' }}
            />
          )}

          {isImage && (
            <div className="p-4">
              <img
                src={document.url}
                alt="معاينة الصورة"
                className="max-w-full h-auto mx-auto"
              />
            </div>
          )}

          {!isPDF && !isImage && (
            <div className="p-8 text-center">
              <p className="text-gray-600 mb-4">
                لا يمكن عرض هذا النوع من الملفات مباشرة
              </p>
              <a
                href={document.url}
                download
                className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                تنزيل الملف
              </a>
            </div>
          )}
        </div>

        <div className="mt-4 text-center text-sm text-gray-500">
          <p>🔒 هذا رابط مؤقت صالح لمدة 24 ساعة</p>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;
